import React, {useEffect, useRef, useState} from 'react';
import {getResource, resetResource, updateResource} from '../../../data/actions/resource';
import LocalStorage from '../../../util/localStorage';
import Resources from '../../../data/services/resources';
import {DocumentTextIcon, LockClosedIcon, TrashIcon} from '@heroicons/react/24/outline';
import {checkPerm, classNames, getDocumentTypesFor, getLookup, getProp} from '../../../common/util/util-helpers';
import {CREATE_PERM, DELETE_PERM, UPDATE_PERM} from '../../../util/util-constants';
import {Field, FieldsManager} from '../../../data/services/fields';
import {fillFieldsFromData} from '../../../common/util/util-fields';
import FieldsForm from '../../../common/components/fields/fields-form';
import Card from '../../../common/components/card';
import Button from '../../../common/components/button';
import {LoaderLarge} from '../../../common/components/loader';
import ModalSaveResource from '../../../common/components/modal/modal-save-resource';
import ModalConfirm from '../../../common/components/modal/modal-confirm';
import NoRecords from "../../../common/components/no-records-found/no-records";
import {scrollErrorIntoViewFields} from "../../../common/util/util-vanilla";
import DraggableGhost from "../../../common/components/draggable-ghost";
import Tooltip from "../../../common/components/tooltip";
import {DragHandleIcon} from '../../../data/themes/icons';
import {CHECKLIST_TEMPLATE_TYPE_CUSTOMER} from "../../../common/util/util-consts";

const EMPTY_LIST = [];

export default function CustomerChecklistTemplateTab({translate, resource, dispatch, setIsTabDirty}) {
    const [selectedItem, setSelectedItem] = useState(null);
    const [fields, setFields] = useState(() => getFields());
    const [isDirty, setIsDirty] = useState(false);
    const [dirtyItemIDs, setDirtyItemIDs] = useState(new Set());
    const [invalidItemIDs, setInvalidItemIDs] = useState(new Set());
    const [newSubStepLabel, setNewSubStepLabel] = useState('');
    const [subStepDraft, setSubStepDraft] = useState(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [subStepToDelete, setSubStepToDelete] = useState(null);
    const [subStepForDocuments, setSubStepForDocuments] = useState(null);
    const [stepsData, setStepsData] = useState([]);
    const [draggingIndex, setDraggingIndex] = useState(-1);
    const [dragOverIndex, setDragOverIndex] = useState(-1);
    const [dragInitialPosition, setDragInitialPosition] = useState({x: 0, y: 0});
    const [draggingSubStepIndex, setDraggingSubStepIndex] = useState(-1);
    const [dragOverSubStepIndex, setDragOverSubStepIndex] = useState(-1);
    const [dragInitialSubStepPosition, setDragInitialSubStepPosition] = useState({x: 0, y: 0});
    const isUpdated = useRef(false);
    const savedFields = useRef(null);
    const savedLocalData = useRef([]);
    const stepsContainerRef = useRef(null);
    const subStepsContainerRef = useRef(null);

    /** Data Events
     ================================================================= */
    const submit = () => {
        let firstInvalidItem = null;
        let firstInvalidFields = null;
        const newInvalidIDs = new Set();

        stepsData.forEach(item => {
            const itemFields = FieldsManager.validateFields(
                item.ChecklistTemplateID === selectedItem?.ChecklistTemplateID ? fields : getFields(item)
            );

            if (!FieldsManager.checkFieldsForErrors(itemFields)) {
                newInvalidIDs.add(item.ChecklistTemplateID);

                if (!firstInvalidItem) {
                    firstInvalidItem = item;
                    firstInvalidFields = itemFields;
                }
            }
        });

        setInvalidItemIDs(newInvalidIDs);

        if (newInvalidIDs.size > 0) {
            setSelectedItem(firstInvalidItem);
            setFields(firstInvalidFields);
            scrollErrorIntoViewFields([firstInvalidFields]);
            return;
        }

        const params = stepsData.map((item, index) => {
            const merged = Object.assign(
                {},
                item,
                item.ChecklistTemplateID === selectedItem?.ChecklistTemplateID ? FieldsManager.getFieldKeyValues(fields) : {},
                {StepOrder: index}
            );

            if (merged.ChecklistTemplateID < 0) {
                delete merged.ChecklistTemplateID;
            }

            if (Array.isArray(merged.ChecklistTemplateSubSteps)) {
                merged.ChecklistTemplateSubSteps = merged.ChecklistTemplateSubSteps.map(subStep => {
                    if (subStep.ChecklistTemplateSubStepID < 0) {
                        const rest = {...subStep};
                        delete rest.ChecklistTemplateSubStepID;
                        return rest;
                    }
                    return subStep;
                });
            }

            return merged;
        });

        setIsDirty(false);
        setDirtyItemIDs(new Set());
        setInvalidItemIDs(new Set());
        isUpdated.current = true;

        dispatch(updateResource({
            user: LocalStorage.get('user'),
            resource: Resources.CustomersChecklistTemplate,
            params: params,
            query: getQuery(),
            piggyResource: Resources.CustomersChecklistTemplate,
            errorMessage: true,
            successMessage: 'Resource updated successfully!'
        }));
    };

    /** UI Events
     ================================================================= */
    const cancel = () => {
        const revertedData = savedLocalData.current;
        const revertedSelected = revertedData.find(item => item.ChecklistTemplateID === selectedItem?.ChecklistTemplateID) ?? revertedData[0] ?? null;
        const revertedFields = getFields(revertedSelected);

        setStepsData(revertedData);
        setSelectedItem(revertedSelected);
        setFields(revertedFields);
        setIsDirty(false);
        setDirtyItemIDs(new Set());
        setInvalidItemIDs(new Set());
    };

    const onInputChange = (name, value) => {
        setFields(prevFields => {
            const fieldsUpdate = FieldsManager.updateField(prevFields, name, value);
            fieldsUpdate[name].errorMessage = "";

            return fieldsUpdate;
        });

        setSelectedItem(prev => ({...prev, [name]: value}));

        setStepsData(prev => prev.map(item =>
            item.ChecklistTemplateID === selectedItem.ChecklistTemplateID ? {...item, [name]: value} : item
        ));

        setDirtyItemIDs(prev => new Set(prev).add(selectedItem.ChecklistTemplateID));
        setInvalidItemIDs(prev => {
            const next = new Set(prev);
            next.delete(selectedItem.ChecklistTemplateID);
            return next;
        });
        setIsDirty(true);
    };

    const handleSelectItem = (item) => {
        const nextFields = getFields(item);
        savedFields.current = nextFields;
        setSelectedItem(item);
        setFields(nextFields);
    };

    const handleAddItem = () => {
        const newItem = {
            ChecklistTemplateID: -Date.now(),
            StepName: '',
            StepOrder: stepsData.length,
            ChecklistTemplateTypeID: CHECKLIST_TEMPLATE_TYPE_CUSTOMER,
            ChecklistTemplateSubSteps: [],
        };

        const nextFields = getFields(newItem, true);
        savedFields.current = nextFields;
        setStepsData(prev => [...prev, newItem]);
        setSelectedItem(newItem);
        setFields(nextFields);
        setIsDirty(true);
    };

    const handleDeleteItem = () => {
        const remaining = stepsData.filter(item => item.ChecklistTemplateID !== selectedItem.ChecklistTemplateID);

        setStepsData(remaining);
        setSelectedItem(remaining[0] ?? null);
        setFields(getFields(remaining[0] ?? null));
        setIsDeleteModalOpen(false);
        setIsDirty(true);
    };

    /** UI Events drag
     ================================================================= */
    const handleMouseDown = (e, index) => {
        setDraggingIndex(index);
        setDragInitialPosition({x: e.clientX, y: e.clientY});
        setDragOverIndex(index);
    };

    const handleDragOver = (e, index) => {
        setDragOverIndex(index);
    };

    const handleDragEnd = () => {
        if (draggingIndex > -1 && dragOverIndex !== -1 && draggingIndex !== dragOverIndex) {
            const reordered = [...stepsData];
            const [moved] = reordered.splice(draggingIndex, 1);
            reordered.splice(dragOverIndex, 0, moved);
            setStepsData(reordered);
            setIsDirty(true);
        }

        setDraggingIndex(-1);
        setDragOverIndex(-1);
    };

    const handleSubStepMouseDown = (e, index) => {
        setDraggingSubStepIndex(index);
        setDragInitialSubStepPosition({x: e.clientX, y: e.clientY});
        setDragOverSubStepIndex(index);
    };

    const handleSubStepDragOver = (e, index) => {
        setDragOverSubStepIndex(index);
    };

    const handleSubStepDragEnd = () => {
        if (draggingSubStepIndex > -1 && dragOverSubStepIndex !== -1 && draggingSubStepIndex !== dragOverSubStepIndex) {
            const reordered = [...checklistSubSteps];
            const [moved] = reordered.splice(draggingSubStepIndex, 1);
            reordered.splice(dragOverSubStepIndex, 0, moved);

            setSelectedItem(prev => ({...prev, ChecklistTemplateSubSteps: reordered}));
            setStepsData(prev => prev.map(item =>
                item.ChecklistTemplateID === selectedItem.ChecklistTemplateID
                    ? {...item, ChecklistTemplateSubSteps: reordered}
                    : item
            ));
            setIsDirty(true);
        }

        setDraggingSubStepIndex(-1);
        setDragOverSubStepIndex(-1);
    };

    /** UI Events sub-steps
     ================================================================= */
    const handleDeleteSubStep = () => {
        const updatedSteps = checklistSubSteps.filter(s => s.ChecklistTemplateSubStepID !== subStepToDelete.ChecklistTemplateSubStepID);

        setSelectedItem(prev => ({...prev, ChecklistTemplateSubSteps: updatedSteps}));
        setStepsData(prev => prev.map(item =>
            item.ChecklistTemplateID === selectedItem.ChecklistTemplateID
                ? {...item, ChecklistTemplateSubSteps: updatedSteps}
                : item
        ));

        if (updatedSteps.length === 0) {
            setFields(prev => {
                prev.AllowIncomplete.disabled = true;
                prev.AllowIncomplete.value = '';
                prev.AutoComplete.disabled = true;
                prev.AutoComplete.value = '';
                return {...prev};
            });
        }

        setSubStepToDelete(null);
        setIsDirty(true);
    };

    const handleUpdateSubStep = () => {
        const label = subStepDraft?.label?.trim();

        if (!label) {
            setSubStepDraft(null);
            return;
        }

        const updatedSteps = checklistSubSteps.map(s =>
            s.ChecklistTemplateSubStepID === subStepDraft.id
                ? {...s, Label: label}
                : s
        );

        setSelectedItem(prev => ({...prev, ChecklistTemplateSubSteps: updatedSteps}));
        setStepsData(prev => prev.map(item =>
            item.ChecklistTemplateID === selectedItem.ChecklistTemplateID
                ? {...item, ChecklistTemplateSubSteps: updatedSteps}
                : item
        ));

        setSubStepDraft(null);
        setIsDirty(true);
    };

    const handleAddSubStep = () => {
        const label = newSubStepLabel?.trim();

        if (!label) return;

        const newStep = {ChecklistTemplateSubStepID: -Date.now(), Label: label, StepOrder: checklistSubSteps.length};
        const updatedSteps = [...checklistSubSteps, newStep];

        setSelectedItem(prev => ({...prev, ChecklistTemplateSubSteps: updatedSteps}));
        setStepsData(prev => prev.map(item =>
            item.ChecklistTemplateID === selectedItem.ChecklistTemplateID
                ? {...item, ChecklistTemplateSubSteps: updatedSteps}
                : item
        ));

        if (updatedSteps.length === 1) {
            setFields(prev => {
                prev.AllowIncomplete.disabled = false;
                prev.AutoComplete.disabled = false;
                return {...prev};
            });
        }

        setNewSubStepLabel('');
        setIsDirty(true);
    };

    /** Helpers
     ================================================================= */
    function getQuery() {
        return {id: CHECKLIST_TEMPLATE_TYPE_CUSTOMER};
    }

    function getRequiredDocumentFields(subStep = null) {
        const fields = {
            RequiredDocumentTypeIDs: new Field('RequiredDocumentTypeIDs', '', [], false, 'select', {
                addContainerClass: 'col-span-full',
                note: "Select a document type if required for this checklist subStep."
            }, {isMulti: true}),
        };

        const mapped = subStep ? {
            ...subStep,
            RequiredDocumentTypeIDs: (subStep.RequiredDocumentTypeIDs ?? []).map(id => ({
                value: id,
                label: DocumentType[id] ?? id
            }))
        } : null;

        return fillFieldsFromData(fields, mapped);
    }

    function getFields(item = null, autoFocusStepName = false) {
        const fields = {
            StepName: new Field('StepName', '', ['empty'], false, 'text', {
                addContainerClass: 'col-span-full',
                autoFocus: autoFocusStepName
            }),
            CanSkipStep: new Field('CanSkipStep', '', [], false, 'checkbox', {
                label: "canSkipStepChecklist",
                addContainerClass: "col-span-full",
                labelType: 'float'
            }),
            AutoComplete: new Field('AutoComplete', '', [], false, 'checkbox', {
                label: "autocomplete_checklist",
                note: "Add at least one step to enable this option.",
                addContainerClass: "col-span-full",
                labelType: 'float'
            }),
            AllowIncomplete: new Field('AllowIncomplete', '', [], false, 'checkbox', {
                label: "allowIncompleteChecklist",
                note: "Add at least one step to enable this option.",
                addContainerClass: "col-span-full",
                labelType: 'float'
            }),
        };

        const fieldsWithData = fillFieldsFromData(fields, item);
        fieldsWithData.AllowIncomplete.disabled = !(item?.ChecklistTemplateSubSteps?.length);
        fieldsWithData.AutoComplete.disabled = !(item?.ChecklistTemplateSubSteps?.length);
        return fieldsWithData;
    }

    const data = getProp(resource, 'data.list', EMPTY_LIST);
    const isLoading = getProp(resource, 'isLoading', true);
    const canCreate = checkPerm(Resources.CustomersChecklistTemplate, CREATE_PERM);
    const companyDocTypes = getDocumentTypesFor('IsCompanyDocument');
    const DocumentType = getLookup('DocumentType');
    const checklistSubSteps = selectedItem?.ChecklistTemplateSubSteps ?? EMPTY_LIST;
    const selects = {
        RequiredDocumentTypeIDs: companyDocTypes,
    };

    /** Life cycle
     ================================================================= */
    useEffect(() => {
        dispatch(getResource({
            user: LocalStorage.get('user'),
            resource: Resources.CustomersChecklistTemplate,
            query: getQuery()
        }));

        return () => {
            dispatch(resetResource());
            isUpdated.current = false;
        };
    }, []);

    useEffect(() => {
        if (Array.isArray(data)) {
            const sorted = [...data].sort((a, b) => (a.StepOrder ?? 0) - (b.StepOrder ?? 0));
            setStepsData(sorted);
            savedLocalData.current = sorted;
        }
    }, [data]);

    useEffect(() => {
        if (stepsData.length && !stepsData.find(item => item.ChecklistTemplateID === selectedItem?.ChecklistTemplateID)) {
            const nextFields = getFields(stepsData[0]);
            savedFields.current = nextFields;
            setSelectedItem(stepsData[0]);
            setFields(nextFields);
        }
    }, [stepsData]);

    useEffect(() => {
        if (setIsTabDirty) {
            setIsTabDirty(isDirty);
        }
    }, [isDirty]);

    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (isDirty) {
                e.preventDefault();
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [isDirty]);

    /** Render
     ================================================================= */
    return (
        <div className="h-full">
            <Card bodyClass={" "} addClass="min-h-[calc(50vh)] relative max-w-6xl mx-auto">
                {isLoading && (
                    <LoaderLarge/>
                )}

                {stepsData.length !== 0 && (
                    <div className="md:grid grid-cols-12 gap-6 min-h-[calc(50vh)] max-h-[calc(70vh)] overflow-y-auto">
                        <div className="col-span-5 pl-6 py-3">
                            <div className="flex items-center justify-between mb-3">
                                <h5 className="text-xl m-0 p-0">Checklist Template</h5>

                                <Button
                                    className="btn btn-outline btn-sm"
                                    hasPerm={checkPerm(Resources.CustomersChecklistTemplate, CREATE_PERM)}
                                    onClick={handleAddItem}
                                >
                                    {translate('btn.add_step')}
                                </Button>
                            </div>

                            <div
                                ref={stepsContainerRef}
                                className="relative"
                                style={{height: stepsData.length * 80 + 'px'}}
                            >
                                {stepsData.map((item, i) => (
                                    <React.Fragment key={item.ChecklistTemplateID}>
                                        {draggingIndex > -1 && i === dragOverIndex - 1 && (
                                            <div
                                                onMouseOver={(e) => handleDragOver(e, i)}
                                                className="absolute w-full z-50"
                                                style={{top: i * 80 + 'px', height: '80px'}}
                                            />
                                        )}

                                        {draggingIndex > -1 && i === dragOverIndex && (
                                            <div
                                                onMouseOver={(e) => handleDragOver(e, i)}
                                                className="absolute w-full z-30"
                                                style={{top: i * 80 + 'px', height: '80px'}}
                                            />
                                        )}

                                        {draggingIndex > -1 && i === dragOverIndex && (
                                            <div className="absolute w-full z-0" style={{top: i * 80 + 'px'}}>
                                                <div
                                                    className="border border-dashed rounded-btn border-tm-gray-400 bg-tm-gray-50/50"
                                                    style={{height: '76px'}}/>
                                            </div>
                                        )}

                                        {draggingIndex > -1 && i === dragOverIndex + 1 && (
                                            <div
                                                onMouseOver={(e) => handleDragOver(e, i)}
                                                className="absolute w-full z-20"
                                                style={{top: i * 80 + 'px', height: '80px'}}
                                            />
                                        )}

                                        <div
                                            onMouseOver={(e) => handleDragOver(e, i)}
                                            className={classNames(
                                                "absolute w-full",
                                                draggingIndex > -1 ? "transition-[translate,opacity] duration-200" : undefined,
                                                draggingIndex > -1 && i <= dragOverIndex && i > draggingIndex && draggingIndex !== i ? "-translate-y-[80px] z-20" : "",
                                                draggingIndex > -1 && i >= dragOverIndex && i < draggingIndex && draggingIndex !== i ? "translate-y-[80px] z-20" : "",
                                                draggingIndex === i ? "opacity-0 z-10" : "",
                                                draggingIndex === -1 ? "z-10" : ""
                                            )}
                                            style={{top: i * 80 + 'px'}}
                                        >
                                            <button
                                                type="button"
                                                onClick={() => handleSelectItem(item)}
                                                className={classNames(
                                                    'border-2 font-bold bg-inverse w-full flex justify-between items-start gap-2 mb-2 pl-2 pr-4 py-2 shadow-xs text-sm font-medium rounded-btn focus:outline-hidden focus-visible:ring-primary focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-popup',
                                                    selectedItem?.ChecklistTemplateID === item.ChecklistTemplateID ? 'text-tm-gray-900 bg-tm-gray-100 border-primary' : 'text-tm-gray-600 border-tm-gray-300',
                                                    invalidItemIDs.has(item.ChecklistTemplateID) && selectedItem?.ChecklistTemplateID !== item.ChecklistTemplateID ? '!bg-red-600/10 !text-red-700 !border-red-400' : ''
                                                )}
                                            >
                                                {stepsData.length > 1 && (
                                                    <DragHandleIcon
                                                        className={classNames("size-5 shrink-0 cursor-grab self-center", draggingIndex > -1 ? "invisible" : undefined)}
                                                        onMouseDown={(e) => {
                                                            e.stopPropagation();
                                                            handleMouseDown(e, i);
                                                        }}
                                                    />
                                                )}

                                                <div className="flex-1 text-left flex flex-col gap-0.5">
                                                    <span className="text-xs font-medium opacity-60">Step {i + 1}</span>

                                                    <span>
                                                        {item.StepName ? item.StepName : <span
                                                            className={classNames(invalidItemIDs.has(item.ChecklistTemplateID) && selectedItem?.ChecklistTemplateID !== item.ChecklistTemplateID ? "text-red-600" : "text-tm-gray-500")}>Please fill in the step name</span>}

                                                        {dirtyItemIDs.has(item.ChecklistTemplateID) && (
                                                            <span className="ml-1 text-primary">*</span>
                                                        )}
                                                    </span>

                                                    <span
                                                        className="text-xs font-normal opacity-50">{item.ChecklistTemplateSubSteps?.length ?? 0} sub-steps</span>
                                                </div>
                                            </button>
                                        </div>
                                    </React.Fragment>
                                ))}

                            </div>
                        </div>

                        <div className="col-span-7 px-6 pb-6 border-l border-tm-gray-300 flex flex-col">
                            {!!selectedItem && (
                                <div>
                                    <div
                                        className="flex items-center justify-between px-2 py-3 mb-3 border-b border-tm-gray-200">
                                        <h5 className="text-xl m-0 p-0">
                                            {selectedItem.StepName}
                                        </h5>

                                        <Button
                                            appearance={'danger-soft'}
                                            hasPerm={checkPerm(Resources.CustomersChecklistTemplate, DELETE_PERM)}
                                            disabled={stepsData.length <= 1}
                                            onClick={() => setIsDeleteModalOpen(true)}
                                        >
                                            {translate('btn.delete_checklist_item')}
                                        </Button>
                                    </div>

                                    <div className="grid gap-4 grid-cols-12">
                                        <FieldsForm
                                            key={selectedItem?.ChecklistTemplateID}
                                            fieldsState={fields}
                                            onChange={onInputChange}
                                            isFormDisabled={isLoading}
                                            translate={translate}
                                            selects={selects}
                                        />
                                    </div>

                                    <div
                                        className="bg-inverse border border-tm-gray-200 rounded-xl max-w-2xl mx-auto overflow-hidden mt-4">

                                        {/* Section header */}
                                        <div
                                            className="flex items-center justify-between px-5 py-4 border-b border-tm-gray-100">
                                            <div className="flex items-center gap-2">
                                                <span
                                                    className="text-sm font-medium text-tm-gray-700">Sub-steps</span>

                                                <span
                                                    className="text-xs text-tm-gray-400 bg-tm-gray-100 px-2 py-0.5 rounded-full">{checklistSubSteps.length} sub-steps</span>
                                            </div>
                                        </div>

                                        {/* Sub-steps list */}
                                        <ul
                                            ref={subStepsContainerRef}
                                            className="relative"
                                            style={{height: checklistSubSteps.length * 48 + 'px'}}
                                        >
                                            {checklistSubSteps.map((subStep, i) => (
                                                <React.Fragment key={subStep.ChecklistTemplateSubStepID}>
                                                    {draggingSubStepIndex > -1 && i === dragOverSubStepIndex - 1 && (
                                                        <div
                                                            onMouseOver={(e) => handleSubStepDragOver(e, i)}
                                                            className="absolute w-full z-50"
                                                            style={{top: i * 48 + 'px', height: '48px'}}
                                                        />
                                                    )}

                                                    {draggingSubStepIndex > -1 && i === dragOverSubStepIndex && (
                                                        <div
                                                            onMouseOver={(e) => handleSubStepDragOver(e, i)}
                                                            className="absolute w-full z-30"
                                                            style={{top: i * 48 + 'px', height: '48px'}}
                                                        />
                                                    )}

                                                    {draggingSubStepIndex > -1 && i === dragOverSubStepIndex && (
                                                        <div className="absolute w-full z-0"
                                                             style={{top: i * 48 + 'px'}}>
                                                            <div
                                                                className="mx-5 my-1 border border-dashed rounded-btn border-tm-gray-400 bg-tm-gray-50/50"
                                                                style={{height: '40px'}}/>
                                                        </div>
                                                    )}

                                                    {draggingSubStepIndex > -1 && i === dragOverSubStepIndex + 1 && (
                                                        <div
                                                            onMouseOver={(e) => handleSubStepDragOver(e, i)}
                                                            className="absolute w-full z-20"
                                                            style={{top: i * 48 + 'px', height: '48px'}}
                                                        />
                                                    )}

                                                    <div
                                                        onMouseOver={(e) => handleSubStepDragOver(e, i)}
                                                        className={classNames(
                                                            "absolute w-full select-none",
                                                            draggingSubStepIndex > -1 ? "transition-[translate,opacity] duration-200" : undefined,
                                                            draggingSubStepIndex > -1 && i <= dragOverSubStepIndex && i > draggingSubStepIndex && draggingSubStepIndex !== i ? "-translate-y-[48px] z-20" : "",
                                                            draggingSubStepIndex > -1 && i >= dragOverSubStepIndex && i < draggingSubStepIndex && draggingSubStepIndex !== i ? "translate-y-[48px] z-20" : "",
                                                            draggingSubStepIndex === i ? "opacity-0 z-10" : "",
                                                            draggingSubStepIndex === -1 ? "z-10" : ""
                                                        )}
                                                        style={{top: i * 48 + 'px'}}
                                                    >
                                                        <li className="flex items-center gap-3 px-5 py-3 group hover:bg-tm-gray-50 border-t border-tm-gray-100 h-12">
                                                            {checklistSubSteps.length > 1 && (
                                                                <div
                                                                    className={classNames("cursor-grab text-tm-gray-400 group-hover:text-tm-gray-500 flex-shrink-0", draggingSubStepIndex > -1 ? "invisible" : undefined)}
                                                                    onMouseDown={(e) => {
                                                                        e.stopPropagation();
                                                                        handleSubStepMouseDown(e, i);
                                                                    }}
                                                                >
                                                                    <DragHandleIcon/>
                                                                </div>
                                                            )}

                                                            <span
                                                                className="text-xs font-medium text-tm-gray-400 w-4 flex-shrink-0">{i + 1}</span>

                                                            <input
                                                                type="text"
                                                                value={subStepDraft?.id === subStep.ChecklistTemplateSubStepID ? subStepDraft.label : subStep.Label}
                                                                onChange={(e) => setSubStepDraft(prev => ({
                                                                    ...prev,
                                                                    label: e.target.value
                                                                }))}
                                                                onFocus={() => setSubStepDraft({
                                                                    id: subStep.ChecklistTemplateSubStepID,
                                                                    label: subStep.Label
                                                                })}
                                                                onBlur={handleUpdateSubStep}
                                                                onKeyDown={(e) => e.key === 'Enter' && handleUpdateSubStep()}
                                                                className="flex-1 text-sm text-tm-gray-700 bg-transparent border-0 outline-none focus:ring-1 focus:ring-primary focus:bg-inverse rounded px-2 py-1 -mx-2 transition-all placeholder:text-tm-gray-300"
                                                                placeholder="Sub-step label..."
                                                            />

                                                            <Tooltip
                                                                disabled={!subStep.RequiredDocumentTypeIDs?.length}
                                                                content={
                                                                    <div className="flex flex-wrap gap-1.5 p-1">
                                                                        {(subStep.RequiredDocumentTypeIDs ?? []).map(id => (
                                                                            DocumentType[id] ?? String(id)
                                                                        )).join(", ")}
                                                                    </div>
                                                                }
                                                            >
                                                                <button
                                                                    type="button"
                                                                    className="relative text-tm-gray-600 hover:text-primary transition-all flex-shrink-0 rounded-btn cursor-pointer"
                                                                    onClick={() => setSubStepForDocuments(subStep)}
                                                                >
                                                                    <DocumentTextIcon className="w-5 h-5"/>

                                                                    {!!subStep.RequiredDocumentTypeIDs?.length && (
                                                                        <span
                                                                            className="absolute -top-1.5 -right-1.5 min-w-[14px] h-[14px] flex items-center justify-center rounded-full bg-primary text-inverse text-[9px] font-bold leading-none px-0.5">
                                                                            {subStep.RequiredDocumentTypeIDs.length}
                                                                        </span>
                                                                    )}
                                                                </button>
                                                            </Tooltip>

                                                            <button
                                                                type="button"
                                                                className="text-tm-gray-600 hover:text-red-500 transition-all flex-shrink-0 rounded-btn cursor-pointer"
                                                                onClick={() => setSubStepToDelete(subStep)}
                                                            >
                                                                <TrashIcon className="w-5 h-5"/>
                                                            </button>
                                                        </li>
                                                    </div>
                                                </React.Fragment>
                                            ))}

                                        </ul>

                                        {/* Add sub-step row (inline) */}
                                        <div
                                            className="flex items-center gap-3 px-5 py-3 border-t border-dashed border-tm-gray-200">
                                            <div className="w-4 flex-shrink-0"></div>

                                            <span
                                                className="text-xs font-medium text-tm-gray-300 w-4 flex-shrink-0">{checklistSubSteps.length + 1}</span>

                                            <input
                                                type="text"
                                                value={newSubStepLabel}
                                                onChange={(e) => setNewSubStepLabel(e.target.value)}
                                                onBlur={handleAddSubStep}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        handleAddSubStep();
                                                    } else if (e.key === 'Tab' && newSubStepLabel.trim()) {
                                                        e.preventDefault();
                                                        handleAddSubStep();
                                                    }
                                                }}
                                                className="flex-1 text-sm text-tm-gray-700 bg-transparent border-0 outline-none focus:ring-1 focus:ring-primary focus:bg-inverse rounded px-2 py-1 -mx-2 transition-all placeholder:text-tm-gray-400"
                                                placeholder="+ Add sub-step"
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {!isLoading && stepsData.length === 0 && (
                    <div className="absolute inset-0 flex items-center justify-center">
                        <NoRecords
                            show={true}
                            title={translate('text.no_checklist_templates')}
                            text={translate('text.no_checklist_templates_create')}
                            btnLabel={translate('btn.add_step')}
                            onBtnClick={handleAddItem}
                            canCreate={canCreate}
                            htmlAfter={!canCreate && (
                                <div className="mt-6 flex items-center gap-2 text-sm text-tm-gray-500">
                                    <LockClosedIcon className="w-4 h-4 shrink-0"/>

                                    <span>{translate('text.no_permission_to_create_checklist_template')}</span>
                                </div>
                            )}
                        />
                    </div>
                )}

                {!!stepsData?.length && (
                    <div className="flex justify-end gap-3 px-4 py-2 border-t border-tm-gray-200">
                        <Button
                            className="btn btn-outline"
                            disabled={!isDirty}
                            onClick={cancel}
                        >
                            {translate('btn.cancel')}
                        </Button>

                        {checkPerm(Resources.CustomersChecklistTemplate, UPDATE_PERM) && (
                            <Button
                                className="btn btn-primary"
                                disabled={!isDirty}
                                onClick={submit}
                            >
                                {translate('btn.save')}
                            </Button>
                        )}
                    </div>
                )}
            </Card>

            <ModalConfirm
                show={isDeleteModalOpen}
                type="default"
                title={translate('dialog_heading.confirm_delete')}
                onClose={() => setIsDeleteModalOpen(false)}
                closeButtonLabel={translate('btn.cancel')}
                buttonLabel={translate('btn.delete')}
                onConfirm={handleDeleteItem}
                translate={translate}
            >
                <p className="text-base">{translate('text.are_you_sure_delete_template', [selectedItem?.StepName])}</p>
            </ModalConfirm>

            <ModalConfirm
                show={!!subStepToDelete}
                type="default"
                title={translate('dialog_heading.confirm_delete')}
                onClose={() => setSubStepToDelete(null)}
                closeButtonLabel={translate('btn.cancel')}
                buttonLabel={translate('btn.delete')}
                onConfirm={handleDeleteSubStep}
                translate={translate}
            >
                <p className="text-base">{translate('text.are_you_sure_delete_template_step', [subStepToDelete?.Label])}</p>
            </ModalConfirm>

            <ModalSaveResource
                title={translate('modal_heading.required_document_for_sub_step', [subStepForDocuments?.Label])}
                widthClass="max-w-3xl"
                gridColsClass="grid-cols-12"
                show={!!subStepForDocuments}
                onClose={() => setSubStepForDocuments(null)}
                fields={getRequiredDocumentFields(subStepForDocuments)}
                onSubmit={(params) => {
                    if (params) {
                        const docIDs = (params.RequiredDocumentTypeIDs ?? []).map(v => (typeof v === 'object' ? v.value : v));

                        const updatedSteps = checklistSubSteps.map(s =>
                            s.ChecklistTemplateSubStepID === subStepForDocuments.ChecklistTemplateSubStepID
                                ? {...s, RequiredDocumentTypeIDs: docIDs}
                                : s
                        );

                        setSelectedItem(prev => ({...prev, ChecklistTemplateSubSteps: updatedSteps}));
                        setStepsData(prev => prev.map(item =>
                            item.ChecklistTemplateID === selectedItem.ChecklistTemplateID
                                ? {...item, ChecklistTemplateSubSteps: updatedSteps}
                                : item
                        ));

                        setIsDirty(true);
                        setSubStepForDocuments(null);
                    }
                }}
                metadata={{
                    RequiredDocumentTypeIDs: companyDocTypes
                }}
                translate={translate}
            />

            <DraggableGhost
                key={`${dragInitialPosition.x}${dragInitialPosition.y}`}
                draggingItem={draggingIndex > -1 ? stepsData[draggingIndex] : null}
                initialPosition={dragInitialPosition}
                onDragEnd={handleDragEnd}
                ghost={
                    <button
                        type="button"
                        className="border-2 font-bold flex justify-between items-start gap-2 pl-2 pr-4 py-2 shadow-xs text-sm font-medium rounded-btn text-tm-gray-900 bg-tm-gray-100 border-primary"
                        style={{width: stepsContainerRef.current?.offsetWidth ?? 'auto'}}
                    >
                        <DragHandleIcon className="invisible size-5 shrink-0 self-center"/>

                        <div className="flex-1 text-left flex flex-col gap-0.5">
                            <span className="text-xs font-medium opacity-60">Step {draggingIndex + 1}</span>

                            <span>{stepsData[draggingIndex]?.StepName}</span>

                            <span
                                className="text-xs font-normal opacity-50">{stepsData[draggingIndex]?.ChecklistTemplateSubSteps?.length ?? 0} sub-steps</span>
                        </div>
                    </button>
                }
            />

            <DraggableGhost
                key={`${dragInitialSubStepPosition.x}${dragInitialSubStepPosition.y}`}
                draggingItem={draggingSubStepIndex > -1 ? checklistSubSteps[draggingSubStepIndex] : null}
                initialPosition={dragInitialSubStepPosition}
                onDragEnd={handleSubStepDragEnd}
                ghost={
                    <div
                        className="flex items-center gap-3 px-5 py-3 bg-inverse border border-tm-gray-200 rounded-lg shadow-md"
                        style={{width: subStepsContainerRef.current?.offsetWidth ?? 'auto'}}>
                        <DragHandleIcon className="invisible h-4 w-4 shrink-0 text-tm-gray-400"/>

                        <span
                            className="flex-1 text-sm text-tm-gray-700">{checklistSubSteps[draggingSubStepIndex]?.Label}</span>
                    </div>
                }
            />
        </div>
    );
}
